import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { formatCurrency, getCSATEmoji } from '@/utils/gameLogic';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock,
  Euro,
  TrendingDown,
  TrendingUp,
  XCircle
} from 'lucide-react';
import { useEffect } from 'react';

export const ResultModal = ({
  customer,
  decisions,
  csat,
  cost,
  responseTime,
  onClose
}) => {
  const getCSATStatus = () => {
    if (csat >= 85) return { label: 'Excellent', color: 'hsl(var(--normal))', icon: CheckCircle2 };
    if (csat >= 70) return { label: 'Good', color: 'hsl(var(--info))', icon: CheckCircle2 };
    if (csat >= 50) return { label: 'Acceptable', color: 'hsl(var(--high))', icon: AlertTriangle };
    return { label: 'Poor', color: 'hsl(var(--critical))', icon: XCircle };
  };

  const status = getCSATStatus();

  // Handle Enter key to continue
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
        // Don't trigger if user is typing in an input field
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
          return;
        }
        
        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
      >
        <Card className="w-full max-w-2xl">
          <CardHeader className="text-center border-b pb-4">
            <CardTitle className="text-3xl">Customer Handled</CardTitle>
            <CardDescription className="text-lg">
              {customer.profile.name} - {status.label} Result
            </CardDescription>
          </CardHeader>

          <CardContent className="p-4 space-y-4 max-h-[85vh] overflow-y-auto">
            {/* CSAT Score */}
            <div className="text-center p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-center gap-3 mb-2">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring' }}
                  className="text-6xl"
                >
                  {getCSATEmoji(csat)}
                </motion.div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Customer Satisfaction Score</p>
                  <div className="flex items-center justify-center gap-4">
                    <motion.p
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.3, type: 'spring' }}
                      className="text-6xl font-bold font-mono"
                      style={{ color: status.color }}
                    >
                      {csat}
                    </motion.p>
                    <div>
                      <p className="text-2xl font-bold" style={{ color: status.color }}>/100</p>
                      <Badge style={{ backgroundColor: status.color }} className="mt-1">
                        {status.label}
                      </Badge>
                    </div>
                  </div>
                </div>
              </div>
              <Progress
                value={csat}
                className="mt-4 h-3"
              />
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-2 gap-3">
              <MetricCard
                icon={<Euro className="w-5 h-5" />}
                label="Total Cost"
                value={formatCurrency(cost)}
                trend={cost < 500 ? 'good' : cost < 800 ? 'neutral' : 'bad'}
              />
              <MetricCard
                icon={<Clock className="w-5 h-5" />}
                label="Response Time"
                value={`${(responseTime / 60).toFixed(1)}m`}
                trend={responseTime < 300 ? 'good' : responseTime < 600 ? 'neutral' : 'bad'}
              />
            </div>

            {/* Decision Summary */}
            <div className="space-y-2">
              <h3 className="font-semibold text-lg">Your Decisions</h3>
              {decisions.transport && (
                <DecisionSummary
                  label="Transport"
                  value={decisions.transport.name}
                  cost={decisions.transport.cost}
                />
              )}
              {decisions.communication && (
                <DecisionSummary
                  label="Communication"
                  value={decisions.communication.name}
                  cost={decisions.communication.cost || 0}
                  showCost={false}
                />
              )}
              {decisions.accommodation && (
                <DecisionSummary
                  label="Accommodation"
                  value={decisions.accommodation.name}
                  cost={decisions.accommodation.cost}
                />
              )}
              {decisions.compensation && (
                <DecisionSummary
                  label="Compensation"
                  value={decisions.compensation.name}
                  cost={decisions.compensation.cost}
                />
              )}
            </div>

            {/* Action Button */}
            <Button
              onClick={onClose}
              className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
              size="default"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
};

const MetricCard = ({ icon, label, value, trend }) => {
  const trendColors = {
    good: 'hsl(var(--normal))',
    neutral: 'hsl(var(--high))',
    bad: 'hsl(var(--critical))'
  };

  const TrendIcon = trend === 'good' ? TrendingUp : trend === 'bad' ? TrendingDown : AlertTriangle;

  return (
    <div className="p-3 bg-muted rounded-lg">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
      </div>
      <div className="flex items-center justify-between">
        <p className="text-2xl font-bold font-mono">{value}</p>
        <TrendIcon className="w-5 h-5" style={{ color: trendColors[trend] }} />
      </div>
    </div>
  );
};

const DecisionSummary = ({ label, value, cost, showCost = true }) => {
  return (
    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium">{value}</p>
      </div>
      {showCost && (
        <p className="font-mono font-semibold">{formatCurrency(cost)}</p>
      )}
    </div>
  );
};